import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { CimaCore, Cloudinary, Util, XmStore, XmStoreUtil } from 'core/services';
import { CmsAccountDataPlanCollection, CmsLevelMoveStart } from 'store/cms/models';
import { StateService } from '@uirouter/core';
import { CONFIG_TOKEN, DataPlanTypePlanEight, DataPlanTypePlanSeven, FeatureID, ICOP_BANNER_LOCATIONS } from 'core/constants';
import { AccountSummary, LineSummary } from 'store/account-summary/models';
import { DiscountPromoCollection } from 'store/promotions/discount-promotion/models';
import { IXMOptions } from 'core/interfaces';
import { SelectedPlanService } from 'shared/services/selected-plan';
import { BillingBannerInfo } from 'store/billing-banner/models';
import { StoreAction } from 'store/actions';

/**
 * Represents the component for starting the level move process.
 * This component is responsible for displaying the initial page data and handling user interactions.
 */
@Component({
    selector: 'xm-level-move-start',
    styleUrls: ['./level-move-start.scss'],
    templateUrl: './level-move-start.html',
    standalone: false
})
export class XmLevelMoveStart implements OnInit, OnDestroy {
    public pageData: CmsLevelMoveStartData = {
        header: '',
        planGroupInfo: []
    };
    public cmsData: CmsLevelMoveStart;
    public imageIcon5G: MediaImageOptions;
    public imageIconPlanSix: MediaImageOptions;
    public imageIconTimeInfo: MediaImageOptions;
    public accountSummary: AccountSummary;
    public activeServicePromo: boolean = false;
    public upgradeBtn: string;
    public isBusinessUser: boolean;
    public enableICOPPhaseOneSegSwitch: boolean;
    public planGroupInfo: CmsLevelMovePlanSixFeaturesGroup[];
    public promoIcon: MediaImageOptions;
    public pageTitle: string;
    public selectedPlans: LevelMoveOverride[];
    public getLinesWithPlans: LineSummary[];
    public bucketId?: string;
    public upgradePlanSevenPlans: CmsUpgradePlanSevenPlans;
    public upgradePlanEightPlans: CmsUpgradePlanSevenPlans; 
    public compareFeatures: CmsCompareFeatures[] = [];
    public matchedBucketSize?: string;
    public dataPlanCollection: CmsAccountDataPlanCollection;
    public promotionsBanner: BillingBanner;
    public isBillPreviewEnabled: boolean;
    public showCompareTable: boolean = false;

    private xmStore: XmStore;
    private subscriptions: Subscription[] = [];
    private state: StateService;
    private cimaCore: CimaCore;
    private planOverrides: LevelMoveOverride[];
    private config: IXMOptions;
    private selectedPlanService: SelectedPlanService;

    constructor(cimaCore: CimaCore, xmStore: XmStore, state: StateService, @Inject(CONFIG_TOKEN) config: IXMOptions, selectedPlanService: SelectedPlanService) {
        Object.assign(this, { cimaCore, xmStore, state, config, selectedPlanService });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.enableICOPPhaseOneSegSwitch = this.config.ENABLE_ICOP_P1_SEGMENTSWITCH;
        this.isBillPreviewEnabled = (this.config.ENABLE_BILL_PREVIEW && !this.isBusinessUser) || (this.config.ENABLE_CBM_BILL_PREVIEW && this.isBusinessUser);
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLevelMoveStart>(CmsLevelMoveStart), (result: CmsLevelMoveStart) => {
            this.cmsData = result;
            this.pageTitle = this.cmsData.planSevenPageTitle;
            this.imageIcon5G = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsData.icon5G)
            };
            this.imageIconPlanSix = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsData.planSixIcon)
            };
            this.imageIconTimeInfo = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsData.timeInfo.icon)
            };
            this.promoIcon = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsData.dollarIcon)
            };
            this.selectedPlans = this.selectedPlanService.getSelectedPlans();
            this.upgradePlanSevenPlans = this.cmsData.planSevenPlanChangeTable.upgradePlanSevenPlans;
            this.upgradePlanEightPlans = this.cmsData.planEightPlanChangeTable.upgradePlanEightPlans;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsAccountDataPlanCollection>(CmsAccountDataPlanCollection), (dataPlanCollection: CmsAccountDataPlanCollection) => {
            this.dataPlanCollection = dataPlanCollection;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (accountSummary: AccountSummary) => {
            this.accountSummary = accountSummary;
            this.bucketId = this.accountSummary.getBtgBucketId();

            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<DiscountPromoCollection>(DiscountPromoCollection), (promoResponse: DiscountPromoCollection) => {
                this.activeServicePromo = promoResponse.isServiceLinePromo || promoResponse.isServiceAccountPromo || promoResponse.isTradeInDDAPromoEligible;
                this.buildPageData();
            }));
        }));

        if (this.enableICOPPhaseOneSegSwitch) {
            this.subscriptions.push(XmStoreUtil.subscribe(
                this.xmStore.find<BillingBannerInfo>(StoreAction.GET_BILLING_BANNER, BillingBannerInfo), (bannerInfo: BillingBannerInfo) => {
                    if (bannerInfo && bannerInfo.banners) {
                        this.promotionsBanner = bannerInfo.banners.find((banner: BillingBanner) => banner.location === ICOP_BANNER_LOCATIONS.SEG_SWITCH_CONFIRMATION);
                    }
                }
            ));
        }

        const matchedPlan: CmsDataPlanDetails = this.dataPlanCollection.plans.find((plan: CmsDataPlanDetails) => plan.bucketId.includes(this.bucketId));
        if (matchedPlan) {
            this.matchedBucketSize = matchedPlan.bucketSize;
        }

        this.mapCompareFeatures();
    }

    /**
     * Cancels the level move and navigates to the landing page.
     */
    public onCancel(): void {
        this.state.go('account.settings.level-move.landing');
    }

    /**
     * Initiates the level move process.
     */
    public startUpgrade(): void {
        this.planOverrides = this.selectedPlans;
        if (this.isBillPreviewEnabled) {
            this.state.go('account.settings.level-move.start.review-order', { planOverrides: this.planOverrides });
            
            return;
        }
        this.state.go('account.settings.level-move.in-progress', { planOverrides: this.planOverrides });
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    /**
     * Returns the media image options for the given icon.
     * @param icon - The CmsMediaImage object representing the icon.
     * @returns The media image options.
     */
    public getImageOption(icon: CmsMediaImage): MediaImageOptions {
        return {
            mobile: Cloudinary.generateMediaFromCms(icon)
        };
    }

    /**
     * Sets the plan overrides for the level move.
     * 
     * @param overrides - An array of level move overrides.
     * @returns void
     */
    public onSelect(overrides: LevelMoveOverride[]): void {
        this.planOverrides = overrides;
    }

    /**
    * Builds the page data based on the conditions and assigns it to the `pageData` property.
    * It iterates through the `planGroupInfo` and `features` to filter and push
    * the relevant content into the `pageData` object.
    * If `isBusinessUser` is true, it populates the plan group information with features specific to business users.
    * If `isBusinessUser` is false, it populates the plan group information with features specific to residential users.
    */
    /**
     * Builds the page data based on the current settings and CMS data.
     * This method populates the pageData object with the appropriate values.
     */
    private buildPageData(): void {  
        if (this.isBusinessUser) {
            this.planGroupInfo = this.cmsData.planSevenCBMGroupInfo;
            this.upgradeBtn = this.accountSummary.level4Customer || this.isBillPreviewEnabled ? this.cmsData.upgradeBtn : this.cmsData.upgradeAndSwitchBtn;
            this.planGroupInfo.forEach((group: CmsLevelMovePlanSixFeaturesGroup, i: number) => {
                const featureArray: CmsLevelMoveFeatureData[] = [];
                if (group.section === FeatureID.GENERIC_PLANS_DETAILS || (!this.accountSummary.isTabletOnlyAccount && group.section === FeatureID.GENERIC_NEW_PLANS)) {
                    // eslint-disable-next-line complexity
                    group.features.forEach((feature: CmsLevelMovePlanSixFeatureData) => {
                        if (feature.id === FeatureID.GENERIC_PHONE_TABLET_WITH_PROMOTIONS && !(this.accountSummary.isTabletOnlyAccount || (this.accountSummary.isTabletWithOtherLinesAccount && !this.activeServicePromo))) {
                            featureArray.push(...feature.content);
                        }
                        if (feature.id === FeatureID.GENERIC_PHONE_TABLET_WITHOUT_PROMOTIONS && (this.accountSummary.isTabletOnlyAccount || (this.accountSummary.isTabletWithOtherLinesAccount && !this.activeServicePromo))) {
                            featureArray.push(...feature.content);
                        }
                        if (feature.id === FeatureID.GENERIC) {
                            featureArray.push(...feature.content);
                        }
                        if (feature.id === FeatureID.TABLET_WITH_OTHER_LINES_WITH_PROMOTIONS && this.accountSummary.isTabletWithOtherLinesAccount && this.activeServicePromo) {
                            featureArray.push(...feature.content);
                        }
                        if (feature.id === FeatureID.TABLET_WITH_OTHER_LINES_WITHOUT_PROMOTIONS && this.accountSummary.isTabletWithOtherLinesAccount && !this.activeServicePromo) {
                            featureArray.push(...feature.content);
                        }
                        if (feature.id === FeatureID.TABLET_ONLY_WITHOUT_PROMOTIONS && this.accountSummary.isTabletOnlyAccount && !this.activeServicePromo) {
                            featureArray.push(...feature.content);
                        }
                        if (feature.id === FeatureID.TABLET_ONLY_WITH_PROMOTIONS && this.accountSummary.isTabletOnlyAccount && this.activeServicePromo) {
                            featureArray.push(...feature.content);
                        }
                        if (feature.id === FeatureID.FIVEG) {
                            featureArray.push(...feature.content);
                        }
                        if (feature.id === FeatureID.RECOMMENDATION_PROMOTION && this.activeServicePromo) {
                            featureArray.push(...feature.content);
                        }
                        if (feature.id === FeatureID.GENERIC_WITH_ATLEAST_ONE_PREMIUM_PLAN) {
                            if (this.selectedPlans?.some((selection: LevelMoveOverride) =>
                                this.upgradePlanSevenPlans?.newPlan?.some((plan: CmsPlanData) => 
                                    (plan.planCode === selection.planId) && (
                                        plan.planName.toUpperCase() === DataPlanTypePlanSeven.UNLIMITED_PREMIUM_FLEX
                                    )
                                )
                            )) {
                                featureArray.push(...feature.content);
                            }
                        }

                    });
                    this.pageData.planGroupInfo[i] = {
                        subheader: group.subheader,
                        features: featureArray
                    };
                }
            });
        } else {
            this.planGroupInfo = this.cmsData.planEightGroupInfo;
            this.upgradeBtn = this.cmsData.upgradeBtn;
            this.planGroupInfo.forEach((group: CmsLevelMovePlanSixFeaturesGroup, i: number) => {
                const featureArray: CmsLevelMoveFeatureData[] = [];
                // eslint-disable-next-line complexity
                group.features.forEach((feature: CmsLevelMovePlanSixFeatureData) => {
                    if (feature.id === FeatureID.GENERIC_PHONE_TABLET_RESIDENTIAL && !this.accountSummary.isTabletOnlyAccount) {
                        featureArray.push(...feature.content);
                    }
                    if (feature.id === FeatureID.GENERIC) {
                        featureArray.push(...feature.content);
                    }
                    if (feature.id === FeatureID.MULTI_PRODUCT_DISCOUNT) {
                        featureArray.push(...feature.content);
                    }
                    if (feature.id === FeatureID.ICOPPhase1 && this.enableICOPPhaseOneSegSwitch) {
                        featureArray.push(...feature.content);
                    }
                    if (feature.id === FeatureID.GENERIC_WITH_ATLEAST_ONE_PREMIUM_UNLIMITED) {
                        if (this.selectedPlans?.some((selection: LevelMoveOverride) =>
                            this.upgradePlanSevenPlans?.newPlan?.some((plan: CmsPlanData) => 
                                (plan.planCode === selection.planId) && plan.planName.toUpperCase() === DataPlanTypePlanSeven.PREMIUM_UNLIMITED
                            )
                        )) {
                            featureArray.push(...feature.content);
                        }
                    }
                    if (feature.id === FeatureID.GENERIC_WITH_ATLEAST_ONE_MOBILE_PLUS) {
                        if (this.selectedPlans?.some((selection: LevelMoveOverride) =>
                            this.upgradePlanEightPlans?.newPlan?.some((plan: CmsPlanData) => 
                                (plan.planCode === selection.planId) && plan.planName.toUpperCase() === DataPlanTypePlanEight.MOBILE_PLUS
                            )
                        )) {
                            featureArray.push(...feature.content);
                        }
                    }
                    if (feature.id === FeatureID.TABLET_WITH_OTHER_LINES_WITH_PROMOTIONS && this.accountSummary.isTabletWithOtherLinesAccount && this.activeServicePromo) {
                        featureArray.push(...feature.content);
                    }
                    if (feature.id === FeatureID.TABLET_WITH_OTHER_LINES_WITHOUT_PROMOTIONS && this.accountSummary.isTabletWithOtherLinesAccount && !this.activeServicePromo) {
                        featureArray.push(...feature.content);
                    }
                    if (feature.id === FeatureID.TABLET_ONLY_WITHOUT_PROMOTIONS && this.accountSummary.isTabletOnlyAccount && !this.activeServicePromo) {
                        featureArray.push(...feature.content);
                    }
                    if (feature.id === FeatureID.TABLET_ONLY_WITH_PROMOTIONS && this.accountSummary.isTabletOnlyAccount && this.activeServicePromo) {
                        featureArray.push(...feature.content);
                    }
                    if (feature.id === FeatureID.FIVEG) {
                        featureArray.push(...feature.content);
                    }
                    if (feature.id === FeatureID.RECOMMENDATION_PROMOTION && this.activeServicePromo) {
                        featureArray.push(...feature.content);
                    }

                });
                this.pageData.planGroupInfo[i] = {
                    subheader: group.subheader,
                    features: featureArray
                };
            });
        }
    }

    private mapCompareFeatures(): void {
        // Determine which upgrade plans to use based on user type
        const upgradePlans: CmsUpgradePlanSevenPlans = !this.isBusinessUser 
            ? this.upgradePlanEightPlans 
            : this.upgradePlanSevenPlans;

        this.compareFeatures = this.accountSummary.getLinesWithPlans
            .filter((line: LineSummary) => this.selectedPlans.some((selection: LevelMoveOverride) => selection.lineId === line.id.value))
            .map((line: LineSummary) => {
                const selectedPlans: LevelMoveOverride = this.selectedPlans.find((selection: LevelMoveOverride) => selection.lineId === line.id.value);
                const newPlanName: string = upgradePlans.newPlan
                    .find((plan: CmsPlanData) => plan.planCode === selectedPlans?.planId)?.planName;
                const newPlanDescription: string = upgradePlans.newPlan.find((plan: CmsPlanData) => plan.planName === newPlanName)?.description;

                const currentPlan: CmsPlanData = upgradePlans.currentPlan.find((plan: CmsPlanData) => plan.planName === line.planName);
                const currentPlanDescription: string = currentPlan?.description;

                return {
                    nickname: line.displayName,
                    currentPlanName: line.planName,
                    currentPlanDescription: currentPlanDescription,
                    newPlanName,
                    newPlanDescription
                };
            });

        this.showCompareTable = this.compareFeatures.length > 0 && this.compareFeatures.some((item: CmsCompareFeatures) => !!item.newPlanName);
    }

}
